import { QuizTopicPageTemplate } from "@/components/QuizTopicPageTemplate";

const tocItems = [
  { id: "why-science-quizzes", title: "Why Science Quizzes?" },
  { id: "general-science", title: "General Science Trivia" },
  { id: "famous-scientists", title: "Famous Scientists" },
  { id: "science-topics", title: "Science Topic Deep Dives" },
];

const quizResources = [
  { name: "Pew Science Quiz", url: "https://www.pewresearch.org/science/quiz/science-knowledge-quiz/", description: "11-question science test" },
  { name: "Science at Random", url: "https://www.britannica.com/quiz/science-at-random-quiz", description: "Random science trivia" },
  { name: "Famous Faces of Science", url: "https://www.britannica.com/quiz/faces-of-science", description: "Identify famous scientists" },
  { name: "General Science Quiz", url: "https://www.britannica.com/quiz/general-science-fact-or-fiction", description: "Fact or fiction science" },
  { name: "Food Science Quiz", url: "https://play.howstuffworks.com/quiz/food-science-quiz", description: "The science of cooking" },
];

const relatedTopics = [
  { name: "History Quizzes", path: "/quizzes/history-quizzes/", description: "Historical events and figures" },
  { name: "Trivia Quizzes", path: "/quizzes/trivia-quizzes/", description: "General knowledge tests" },
  { name: "Food Quizzes", path: "/quizzes/food-quizzes/", description: "Food science and cooking" },
];

const ScienceQuizzes = () => {
  return (
    <QuizTopicPageTemplate
      title="Science Quizzes: Test Your Scientific Knowledge"
      metaTitle="Science Quizzes | Science Trivia & Knowledge Tests 2025"
      metaDescription="Challenge your science knowledge with quizzes on biology, physics, chemistry, and astronomy. From basic facts to famous scientists - how much do you know?"
      keywords="science quizzes, science trivia, biology quiz, physics quiz, chemistry quiz, science knowledge test, scientist quiz"
      canonicalUrl="/quizzes/science-quizzes/"
      category="Science"
      publishDate="January 12, 2025"
      readTime="5 min read"
      tocItems={tocItems}
      leadParagraph="Science explains how our world works—from the tiniest atoms to the vastness of space. Science quizzes test your understanding of biology, chemistry, physics, and the discoveries that have shaped human knowledge."
      quizResources={quizResources}
      relatedTopics={relatedTopics}
    >
      <section id="why-science-quizzes" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Why <span className="text-gradient">Science Quizzes?</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Whether you're a science enthusiast or just curious about how things work, science quizzes help you test and expand your understanding of the natural world.
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Refresh knowledge:</strong> Remember what you learned in school</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Learn new facts:</strong> Discover fascinating scientific discoveries</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Prepare for tests:</strong> Great for science class exam prep</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Bust myths:</strong> Separate science fact from fiction</span>
          </li>
        </ul>
      </section>

      <section id="general-science" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          General Science <span className="text-gradient">Trivia</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          The <a href="https://www.pewresearch.org/science/quiz/science-knowledge-quiz/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Pew Research Center's Science Knowledge Quiz</a> is one of the most popular science tests online. It covers 11 questions on basic scientific concepts that every adult should know:
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span>What causes the seasons on Earth?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span>Which gas makes up most of Earth's atmosphere?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span>What does DNA stand for?</span>
          </li>
        </ul>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Britannica's <a href="https://www.britannica.com/quiz/general-science-fact-or-fiction" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">General Science: Fact or Fiction</a> quiz challenges common misconceptions about science. Can you tell which "facts" are actually myths?
        </p>
      </section>

      <section id="famous-scientists" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Famous <span className="text-gradient">Scientists</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          <a href="https://www.britannica.com/quiz/faces-of-science" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Famous Faces of Science</a> from Britannica tests whether you can identify the scientists who changed our understanding of the universe:
        </p>
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Albert Einstein</h4>
            <p className="text-sm text-muted-foreground">Theory of relativity</p>
          </div>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Marie Curie</h4>
            <p className="text-sm text-muted-foreground">Radioactivity pioneer</p>
          </div>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Isaac Newton</h4>
            <p className="text-sm text-muted-foreground">Laws of motion and gravity</p>
          </div>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Charles Darwin</h4>
            <p className="text-sm text-muted-foreground">Theory of evolution</p>
          </div>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Stephen Hawking</h4>
            <p className="text-sm text-muted-foreground">Black holes and cosmology</p>
          </div>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Nikola Tesla</h4>
            <p className="text-sm text-muted-foreground">Electrical engineering genius</p>
          </div>
        </div>
      </section>

      <section id="science-topics" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Science Topic <span className="text-gradient">Deep Dives</span>
        </h2>
        
        <h3 className="text-2xl font-display font-semibold mt-8 mb-4">Biology</h3>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Test your knowledge of living organisms—from cell structure to ecosystems. Can you name the parts of a cell? Explain how photosynthesis works? The <a href="https://www.britannica.com/quiz/science-at-random-quiz" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Science at Random Quiz</a> includes biology questions.
        </p>

        <h3 className="text-2xl font-display font-semibold mt-8 mb-4">Chemistry</h3>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          The periodic table, chemical reactions, and atomic structure. Do you know your noble gases from your halogens?
        </p>

        <h3 className="text-2xl font-display font-semibold mt-8 mb-4">Physics</h3>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Forces, energy, and the fundamental laws of the universe. Can you explain Newton's three laws of motion?
        </p>

        <h3 className="text-2xl font-display font-semibold mt-8 mb-4">Food Science</h3>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          <a href="https://play.howstuffworks.com/quiz/food-science-quiz" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">The Ultimate Food Science Quiz</a> from HowStuffWorks covers the chemistry of cooking—from why bread rises to why onions make you cry.
        </p>
      </section>
    </QuizTopicPageTemplate>
  );
};

export default ScienceQuizzes;
